/**
 * SuperAdminAuditLogCard — Feb 2026 spreadsheet-style audit table.
 *
 * Server-side sortable / paginated / filterable table over
 * `/api/admin/audit/events`. Every column header is a two-state toggle:
 *   click 1 → DESC   ▼
 *   click 2 → ASC    ▲
 *
 * "Download CSV" streams the current filter+sort from
 * `/api/admin/audit/events.csv` (up to 10k rows per download).
 *
 * "Print Log" opens `window.print()` after adding a `.audit-print-mode`
 * class on <html> so global CSS strips sidebar/nav.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowDown, ArrowUp, ArrowUpDown, Download, Printer, RefreshCw, Search, X,
} from "lucide-react";

const COLUMNS = [
    { key: "created_at",     label: "Timestamp (UTC)", sortable: true,  className: "min-w-[190px] font-mono text-[11px]" },
    { key: "event_type",     label: "Event type",      sortable: true,  className: "min-w-[130px]" },
    { key: "user_email",     label: "Actor",           sortable: false, className: "min-w-[220px]" },
    { key: "target_email",   label: "Target",          sortable: false, className: "min-w-[220px]" },
    { key: "duration_ms",    label: "Duration",        sortable: true,  className: "min-w-[80px]  text-right font-mono proportional-nums" },
    { key: "summary",        label: "Payload",         sortable: false, className: "min-w-[420px]" },
];

const PAGE_SIZES = [25, 50, 100, 200];

export default function SuperAdminAuditLogCard() {
    const [rows,        setRows]        = useState([]);
    const [total,       setTotal]       = useState(0);
    const [page,        setPage]        = useState(1);
    const [pageSize,    setPageSize]    = useState(50);
    const [sortBy,      setSortBy]      = useState("created_at");
    const [order,       setOrder]       = useState("desc");
    const [eventType,   setEventType]   = useState("");
    const [sinceHours,  setSinceHours]  = useState(168);
    const [q,           setQ]           = useState("");
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState(null);
    const [eventTypes,  setEventTypes]  = useState([]);
    const [expanded,    setExpanded]    = useState(null);

    // Load enum on mount
    useEffect(() => {
        api.get("/admin/audit/event-types")
            .then((r) => setEventTypes(r.data.event_types || []))
            .catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({
                page: String(page), page_size: String(pageSize),
                sort_by: sortBy, order,
                since_hours: String(sinceHours),
            });
            if (eventType) params.set("event_type", eventType);
            if (q.trim())  params.set("q", q.trim());
            const { data } = await api.get(`/admin/audit/events?${params}`);
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error("Failed to load audit log.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, sortBy, order, eventType, sinceHours, q]);

    useEffect(() => { load(); }, [load]);

    const onHeaderClick = (col) => {
        if (!col.sortable) return;
        if (sortBy === col.key) {
            setOrder(order === "desc" ? "asc" : "desc");
        } else {
            setSortBy(col.key);
            setOrder("desc"); // first click = highest→lowest per spec
        }
        setPage(1);
    };

    const onDownloadCsv = async () => {
        const params = new URLSearchParams({
            sort_by: sortBy, order, since_hours: String(sinceHours),
        });
        if (eventType) params.set("event_type", eventType);
        if (q.trim())  params.set("q", q.trim());
        try {
            const res = await api.get(`/admin/audit/events.csv?${params}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `melian_audit_log_${new Date().toISOString().replace(/[:.]/g,'-')}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded.");
        } catch (e) {
            toast.error("CSV download failed.");
        }
    };

    const onPrint = () => {
        document.documentElement.classList.add("audit-print-mode");
        setTimeout(() => {
            window.print();
            setTimeout(() => document.documentElement.classList.remove("audit-print-mode"), 500);
        }, 100);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="space-y-3" data-testid="audit-log-card">
            {/* ── Toolbar ── */}
            <div className="flex flex-wrap items-center gap-2 no-print">
                <div className="flex items-center gap-1.5 flex-1 min-w-[220px] px-2.5 h-9 rounded border border-border bg-card">
                    <Search className="w-3.5 h-3.5 text-muted-foreground" />
                    <input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Search prompts / symbols / posts…"
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        data-testid="audit-search"
                    />
                    {q && <button onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
                </div>

                <select
                    value={eventType}
                    onChange={(e) => { setEventType(e.target.value); setPage(1); }}
                    className="h-9 px-2 rounded border border-border bg-card text-xs font-mono"
                    data-testid="audit-filter-event-type"
                >
                    <option value="">All events</option>
                    {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>

                <select
                    value={sinceHours}
                    onChange={(e) => { setSinceHours(parseInt(e.target.value)); setPage(1); }}
                    className="h-9 px-2 rounded border border-border bg-card text-xs"
                    data-testid="audit-filter-window"
                >
                    <option value="1">Last hour</option>
                    <option value="24">Last 24h</option>
                    <option value="168">Last 7d</option>
                    <option value="720">Last 30d</option>
                    <option value="8760">Last year</option>
                </select>

                <button
                    onClick={load} disabled={loading}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-border hover:bg-muted/50 text-xs font-semibold disabled:opacity-50"
                    data-testid="audit-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
                <button
                    onClick={onDownloadCsv}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded bg-primary text-primary-foreground hover:brightness-110 text-xs font-semibold"
                    data-testid="audit-download-csv"
                >
                    <Download className="w-3 h-3" />
                    Download CSV
                </button>
                <button
                    onClick={onPrint}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-border hover:bg-muted/50 text-xs font-semibold"
                    data-testid="audit-print"
                >
                    <Printer className="w-3 h-3" />
                    Print Log
                </button>
            </div>

            {error && (
                <div className="p-3 rounded border border-destructive/40 bg-destructive/5 text-xs text-destructive" data-testid="audit-error">
                    {error}
                </div>
            )}

            {/* ── Table ── */}
            <div className="border border-border rounded-xl bg-card overflow-hidden audit-print-scope">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="audit-table">
                        <thead className="bg-muted/40">
                            <tr>
                                {COLUMNS.map((c) => (
                                    <th
                                        key={c.key}
                                        onClick={() => onHeaderClick(c)}
                                        data-testid={`audit-header-${c.key}`}
                                        data-sort={sortBy === c.key ? order : "none"}
                                        className={`text-left align-middle px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest font-bold ${c.className} ${c.sortable ? "cursor-pointer hover:text-foreground select-none" : ""}`}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {c.label}
                                            {c.sortable && (
                                                sortBy === c.key
                                                    ? (order === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                                                    : <ArrowUpDown className="w-3 h-3 opacity-40" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rows.length === 0 && (
                                <tr><td colSpan={COLUMNS.length} className="p-6 text-center text-xs text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={COLUMNS.length} className="p-6 text-center text-xs text-muted-foreground" data-testid="audit-empty">No events in window.</td></tr>
                            )}
                            {rows.map((r) => (
                                <AuditRow key={r._id} row={r}
                                          expanded={expanded === r._id}
                                          onToggle={() => setExpanded(expanded === r._id ? null : r._id)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Pagination ── */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs no-print" data-testid="audit-pagination">
                <div className="text-muted-foreground">
                    {total.toLocaleString()} events · page {page} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
                        className="h-8 px-2 rounded border border-border bg-card text-xs"
                        data-testid="audit-page-size"
                        aria-label="Page size"
                    >
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{`${s}/page`}</option>)}
                    </select>
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                        className="h-8 px-3 rounded border border-border hover:bg-muted/50 disabled:opacity-40"
                        data-testid="audit-prev"
                    >← Prev</button>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                        className="h-8 px-3 rounded border border-border hover:bg-muted/50 disabled:opacity-40"
                        data-testid="audit-next"
                    >Next →</button>
                </div>
            </div>
        </div>
    );
}


function AuditRow({ row, expanded, onToggle }) {
    const summary = useMemo(() => summarise(row), [row]);
    const duration = row.duration_ms != null ? `${row.duration_ms} ms` : "";
    return (
        <>
            <tr
                onClick={onToggle}
                className="border-b border-border hover:bg-muted/30 cursor-pointer"
                data-testid={`audit-row-${row._id}`}
            >
                <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{row.created_at?.replace("T", " ").slice(0, 19)}</td>
                <td className="px-3 py-2">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px]">{row.event_type}</span>
                </td>
                <td className="px-3 py-2 truncate max-w-[220px]" title={row.user_email || row.user_id}>
                    {row.user_email || <span className="text-muted-foreground font-mono">{row.user_id || "—"}</span>}
                </td>
                <td className="px-3 py-2 truncate max-w-[220px]" title={row.target_email || row.target_user_id || ""}>
                    {row.target_email || (row.target_user_id ? <span className="text-muted-foreground font-mono">{row.target_user_id}</span> : "—")}
                </td>
                <td className="px-3 py-2 text-right font-mono proportional-nums">{duration}</td>
                <td className="px-3 py-2 truncate max-w-[420px]" title={summary}>{summary}</td>
            </tr>
            {expanded && (
                <tr className="bg-muted/20">
                    <td colSpan={6} className="px-3 py-3">
                        <pre className="text-[11px] font-mono whitespace-pre-wrap break-words max-h-[300px] overflow-auto" data-testid={`audit-payload-${row._id}`}>
                            {JSON.stringify(row.payload, null, 2)}
                        </pre>
                    </td>
                </tr>
            )}
        </>
    );
}

/** Client-side row summary — mirrors the server's _summarise() for consistency. */
function summarise(evt) {
    const p = evt.payload || {};
    const et = evt.event_type || "";
    if (et.startsWith("social_"))    return `target=${evt.target_user_id || "?"}`;
    if (et === "referral_share")     return `channel=${p.channel} code=${p.referral_code}`;
    if (et === "trade_open" || et === "trade_close") {
        return `${p.side} ${p.qty} ${p.symbol} @ ${p.price} → ${p.status}`;
    }
    if (et === "ai_kimani" || et === "ai_obondo") {
        return `prompt: ${(p.prompt || "").slice(0, 140)}`;
    }
    if (et === "community_post") {
        return `scope=${p.scope} tags=${(p.tags || []).join(",")} text: ${(p.text || "").slice(0, 140)}`;
    }
    if (et === "dm_sent") {
        return `to=${evt.target_user_id} len=${p.message_length}`;
    }
    return JSON.stringify(p).slice(0, 200);
}
